import { hash2 } from '../core/noise';

export const BIOME_NAMES = ['lanternwood', 'blueIce', 'timberline'] as const;

export type BiomeName = (typeof BIOME_NAMES)[number];
export type BiomeWeights = Record<BiomeName, number>;
export type BiomeColor = readonly [red: number, green: number, blue: number];

export interface BiomeTerrainProfile {
  /** Strength of broad, soft downhill undulations. */
  readonly rolling: number;
  /** Strength of cross-slope hills and folded ridges. */
  readonly ridges: number;
  /** Strength of broad wind-deposited snow forms. */
  readonly drifts: number;
  /** Strength of small terrain detail under the skis. */
  readonly microRoughness: number;
}

export interface BiomeWeatherProfile {
  /** Relative amount of visible falling snow. */
  readonly snowfall: number;
  /** Relative strength of wind and horizontal spindrift. */
  readonly wind: number;
}

export interface BiomeProfile {
  readonly id: BiomeName;
  readonly label: string;
  readonly description: string;
  readonly terrainColor: BiomeColor;
  readonly shadowColor: BiomeColor;
  readonly rockColor: BiomeColor;
  readonly accentColor: BiomeColor;
  readonly skyColor: BiomeColor;
  readonly fogColor: BiomeColor;
  readonly sunColor: BiomeColor;
  readonly terrain: BiomeTerrainProfile;
  readonly fogNear: number;
  readonly fogFar: number;
  readonly weather: BiomeWeatherProfile;
}

export const BIOME_PROFILES = {
  lanternwood: {
    id: 'lanternwood',
    label: 'Lanternwood Glade',
    description: 'Warm powder glades winding through friendly firs, pale birch, and sheltered woodland clearings.',
    terrainColor: [0.961, 0.945, 0.886],
    shadowColor: [0.725, 0.839, 0.812],
    rockColor: [0.47, 0.50, 0.47],
    accentColor: [0.92, 0.58, 0.25],
    skyColor: [0.769, 0.863, 0.855],
    fogColor: [0.769, 0.863, 0.855],
    sunColor: [1, 0.816, 0.541],
    terrain: {
      rolling: 0.92,
      ridges: 0.75,
      drifts: 1.12,
      microRoughness: 0.75,
    },
    fogNear: 84,
    fogFar: 305,
    weather: {
      snowfall: 0.52,
      wind: 0.16,
    },
  },
  blueIce: {
    id: 'blueIce',
    label: 'Blue Ice Garden',
    description: 'A luminous glacier of broad blue swells, frozen cascades, and crystalline ice formations.',
    terrainColor: [0.769, 0.918, 0.945],
    shadowColor: [0.412, 0.741, 0.824],
    rockColor: [0.447, 0.533, 0.584],
    accentColor: [0.69, 0.94, 1],
    skyColor: [0.588, 0.8, 0.878],
    fogColor: [0.588, 0.8, 0.878],
    sunColor: [0.925, 1, 1],
    terrain: {
      rolling: 0.72,
      ridges: 0.46,
      drifts: 0.28,
      microRoughness: 0.3,
    },
    fogNear: 104,
    fogFar: 340,
    weather: {
      snowfall: 0.2,
      wind: 0.34,
    },
  },
  timberline: {
    id: 'timberline',
    label: 'Windswept Timberline',
    description: 'An exposed high ridge carved into stone ledges, long snow fingers, and wind-combed crust.',
    terrainColor: [0.906, 0.918, 0.925],
    shadowColor: [0.659, 0.706, 0.761],
    rockColor: [0.408, 0.447, 0.471],
    accentColor: [0.851, 0.416, 0.329],
    skyColor: [0.667, 0.714, 0.776],
    fogColor: [0.667, 0.714, 0.776],
    sunColor: [1, 0.878, 0.745],
    terrain: {
      rolling: 0.94,
      ridges: 1.22,
      drifts: 1.36,
      microRoughness: 1.16,
    },
    fogNear: 58,
    fogFar: 248,
    weather: {
      snowfall: 0.42,
      wind: 1,
    },
  },
} as const satisfies Record<BiomeName, BiomeProfile>;

/** Base snow colors retained as a focused terrain-rendering lookup. */
export const BIOME_COLORS: Record<BiomeName, BiomeColor> = {
  lanternwood: BIOME_PROFILES.lanternwood.terrainColor,
  blueIce: BIOME_PROFILES.blueIce.terrainColor,
  timberline: BIOME_PROFILES.timberline.terrainColor,
};

/** Stable world-space locations used by the deterministic environment gallery. */
export const BIOME_PREVIEW_ORIGINS: Record<BiomeName, Readonly<{ x: number; z: number }>> = {
  lanternwood: { x: 0, z: -1_200 },
  blueIce: { x: 3_000, z: -2_400 },
  timberline: { x: 720, z: -2_880 },
};

const FIELD_SCALE_X = 960;
const FIELD_SCALE_Z = 1_380;
const FIELD_OFFSET_X = 420;
const FIELD_OFFSET_Z = 900;
const FIELD_SHARPNESS = 8;

function emptyWeights(): BiomeWeights {
  return {
    lanternwood: 0,
    blueIce: 0,
    timberline: 0,
  };
}

/**
 * Smooth deterministic biome weights in virtual world coordinates.
 *
 * Every cell owns one jittered biome source. Summing nearby radial influences
 * makes boundaries broad enough to traverse naturally, while staying entirely
 * independent of render chunks and the floating origin.
 */
export function biomeWeightsAt(x: number, z: number): BiomeWeights {
  const scaledX = (x + FIELD_OFFSET_X) / FIELD_SCALE_X;
  const scaledZ = (z + FIELD_OFFSET_Z) / FIELD_SCALE_Z;
  const cellX = Math.floor(scaledX);
  const cellZ = Math.floor(scaledZ);
  const weights = emptyWeights();
  let total = 0;

  for (let offsetZ = -2; offsetZ <= 2; offsetZ += 1) {
    for (let offsetX = -2; offsetX <= 2; offsetX += 1) {
      const gridX = cellX + offsetX;
      const gridZ = cellZ + offsetZ;
      const pointX = gridX + 0.15 + hash2(gridX, gridZ, 171) * 0.7;
      const pointZ = gridZ + 0.15 + hash2(gridX, gridZ, 293) * 0.7;
      const dx = scaledX - pointX;
      const dz = scaledZ - pointZ;
      const influence = Math.exp(-(dx * dx + dz * dz) * FIELD_SHARPNESS);
      const biomeIndex = Math.floor(hash2(gridX, gridZ, 419) * BIOME_NAMES.length);
      const biome = BIOME_NAMES[Math.min(biomeIndex, BIOME_NAMES.length - 1)]!;
      weights[biome] += influence;
      total += influence;
    }
  }

  if (total < 0.000_001) {
    weights.lanternwood = 1;
    return weights;
  }

  for (const name of BIOME_NAMES) weights[name] /= total;
  return weights;
}

export function dominantBiome(weights: BiomeWeights): BiomeName {
  let dominant: BiomeName = 'lanternwood';
  for (const name of BIOME_NAMES) {
    if (weights[name] > weights[dominant]) dominant = name;
  }
  return dominant;
}
